/**
 * Provider-aware chat client for AEGIS.
 *
 * API keys are stored encrypted per-team and are NEVER shipped to the browser.
 * Instead we POST to a server-side proxy that injects the team's key and relays
 * the provider response verbatim:
 *   • Groq      → `/teams/:id/agent_chat`        (OpenAI chat-completions schema)
 *   • Anthropic → `/teams/:id/agent_chat_claude` (Anthropic Messages schema)
 *
 * Internally the whole tool-calling loop is written against the OpenAI shape
 * (it predates Claude support). For Anthropic we translate the request to the
 * Messages API on the way out and translate the response back to the OpenAI
 * shape on the way in (see toAnthropicRequest / fromAnthropicResponse), so the
 * loop itself stays provider-agnostic.
 */

import type {
  AgentApiMessage,
  AgentCompletionResult,
  AgentKeySource,
  AgentModelOption,
  AgentProviderId,
  AgentToolCall,
} from './types'

export const DEFAULT_AGENT_MODEL = "qwen/qwen3-32b"

/** Max tokens to request from Anthropic (Groq doesn't require this field). */
export const ANTHROPIC_MAX_TOKENS = 4096

export function normalizeProvider(provider: string | undefined): AgentProviderId {
  if (provider === "anthropic") return "anthropic"
  if (provider === "deepseek") return "deepseek"
  return "groq"
}

export function agentProviderLabel(provider: AgentProviderId): string {
  switch (provider) {
    case "anthropic":
      return "Claude"
    case "deepseek":
      return "DeepSeek"
    default:
      return "Groq"
  }
}

/** Build the team-scoped proxy endpoint for a given team id + provider. */
export function agentChatEndpoint(teamId: number, provider: AgentProviderId): string {
  if (provider === "anthropic") return `/teams/${teamId}/agent_chat_claude`
  if (provider === "deepseek") return `/teams/${teamId}/agent_chat_deepseek`
  return `/teams/${teamId}/agent_chat`
}

function normalizeAgentModelOption(raw: any): AgentModelOption | null {
  const provider = normalizeProvider(raw?.provider)
  const keySource = raw?.key_source === "team" ? "team" : "personal"
  const model = String(raw?.model || "").trim()
  const id = String(raw?.id || "").trim()
  if (!id || !model) return null
  return {
    id,
    provider,
    providerLabel: String(raw?.provider_label || provider),
    keySource,
    keySourceLabel: String(raw?.key_source_label || (keySource === "team" ? "Team key" : "Personal key")),
    model,
    modelLabel: String(raw?.model_label || model),
    label: String(raw?.label || `${provider} · ${model}`),
  }
}

/** Load every provider/model/key-source combo available to the current user. */
export async function fetchAgentModelOptions(teamId: number): Promise<{
  options: AgentModelOption[]
  defaultId: string | null
}> {
  const response = await fetch(`/teams/${teamId}/agent_model_options`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  })
  if (!response.ok) {
    throw new Error(`Failed to load agent models (${response.status})`)
  }
  const data = await response.json()
  const options = (Array.isArray(data.options) ? data.options : [])
    .map(normalizeAgentModelOption)
    .filter((option): option is AgentModelOption => option !== null)
  const defaultId =
    typeof data.default_id === "string" && options.some(option => option.id === data.default_id)
      ? data.default_id
      : options[0]?.id ?? null
  return { options, defaultId }
}

export function agentModelOptionStorageKey(teamId: number): string {
  return `aegis-model-option:${teamId}`
}


/**
 * Translate an OpenAI-style request body into the Anthropic Messages API
 * shape. Handles the system prompt (top-level `system` param), tool
 * definitions (`input_schema` instead of nested `function`), assistant
 * tool-call turns (`tool_use` content blocks) and tool results (`tool_result`
 * blocks carried on a synthetic user turn).
 */
export function toAnthropicRequest(body: Record<string, any>): Record<string, any> {
  const messages: AgentApiMessage[] = Array.isArray(body.messages) ? body.messages : []
  let system = ""
  const out: Array<{ role: "user" | "assistant"; content: any; _toolResult?: boolean }> = []

  for (const m of messages) {
    if (m.role === "system") {
      system += (system ? "\n\n" : "") + (m.content ?? "")
      continue
    }
    if (m.role === "tool") {
      // Tool results are delivered to Anthropic as user-turn content blocks.
      // Merge consecutive results into a single user turn so the
      // user/assistant alternation the API requires is preserved.
      const block = {
        type: "tool_result",
        tool_use_id: (m as any).tool_call_id,
        content: m.content ?? "",
      }
      const last = out[out.length - 1]
      if (last && last.role === "user" && last._toolResult && Array.isArray(last.content)) {
        last.content.push(block)
      } else {
        out.push({ role: "user", content: [block], _toolResult: true })
      }
      continue
    }
    if (m.role === "assistant") {
      const content: any[] = []
      if (typeof m.content === "string" && m.content.trim()) {
        content.push({ type: "text", text: m.content })
      }
      const toolCalls = (m as any).tool_calls as AgentToolCall[] | undefined
      if (Array.isArray(toolCalls)) {
        for (const tc of toolCalls) {
          let input: unknown = {}
          try {
            input = JSON.parse(tc.function?.arguments || "{}")
          } catch {
            input = {}
          }
          content.push({ type: "tool_use", id: tc.id, name: tc.function?.name, input })
        }
      }
      // Anthropic rejects empty content; fall back to a single space if a
      // (degenerate) assistant turn carried neither text nor tool calls.
      out.push({ role: "assistant", content: content.length ? content : " " })
      continue
    }
    // user
    out.push({ role: "user", content: m.content ?? "" })
  }

  for (const m of out) delete m._toolResult

  // Anthropic requires the first message to use the `user` role. Our chat
  // history opens with an assistant greeting (INITIAL_CHAT_MESSAGES), so drop
  // any leading assistant turns before sending.
  while (out.length && out[0].role === "assistant") out.shift()

  const req: Record<string, any> = {
    model: body.model,
    max_tokens: ANTHROPIC_MAX_TOKENS,
    messages: out,
  }
  // Note: `temperature` is intentionally omitted. The latest Claude models
  // (e.g. Opus 4.8) reject it with a 400 ("temperature is deprecated for this
  // model"), and Anthropic's default sampling is fine for the agent loop.
  if (system) req.system = system

  if (Array.isArray(body.tools)) {
    req.tools = body.tools.map((t: any) => ({
      name: t.function?.name,
      description: t.function?.description,
      input_schema: t.function?.parameters,
    }))
    // Mirror OpenAI's `tool_choice: "auto"` + batched (parallel) tool calls,
    // so Claude can emit a whole plan (add + connect + name) in one response
    // — symbolic refs are resolved client-side in call order.
    req.tool_choice = { type: "auto", disable_parallel_tool_use: false }
  }

  return req
}

/**
 * Translate an Anthropic Messages API response back into the OpenAI
 * chat-completions shape the tool loop expects. Concatenates text blocks,
 * maps `tool_use` blocks to `tool_calls`, and surfaces any `thinking` blocks
 * as `message.reasoning`.
 */
export function fromAnthropicResponse(raw: any): any {
  const blocks: any[] = Array.isArray(raw?.content) ? raw.content : []
  let text = ""
  let reasoning = ""
  const toolCalls: AgentToolCall[] = []

  for (const b of blocks) {
    if (b?.type === "text") {
      text += b.text ?? ""
    } else if (b?.type === "thinking") {
      reasoning += b.thinking ?? ""
    } else if (b?.type === "tool_use") {
      toolCalls.push({
        id: b.id,
        type: "function",
        function: { name: b.name, arguments: JSON.stringify(b.input ?? {}) },
      })
    }
  }

  const message: any = { role: "assistant", content: text }
  if (toolCalls.length) message.tool_calls = toolCalls
  if (reasoning.trim()) message.reasoning = reasoning.trim()
  return { choices: [{ message }] }
}

/** Read the Rails CSRF token from the page's meta tag (set on every view). */
export function csrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  return el?.content ?? ""
}

export async function requestAgentCompletion(
  teamId: number | undefined,
  provider: AgentProviderId,
  body: Record<string, unknown>,
  keySource?: AgentKeySource,
  signal?: AbortSignal,
  shareCode?: string
): Promise<AgentCompletionResult> {
  const payload = prepareOutgoingChatBody(provider, body)
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-CSRF-Token": csrfToken(),
  }
  if (keySource && !shareCode) headers["X-Agent-Key-Source"] = keySource
  const endpoint = shareCode
    ? `/share/${encodeURIComponent(shareCode)}/agent_chat`
    : agentChatEndpoint(teamId as number, provider)

  const response = await fetch(endpoint, {
    method: "POST",
    headers,
    // Same-origin so the session cookie authenticates the team membership
    // check server-side; the proxy adds the provider auth header itself.
    credentials: "same-origin",
    body: JSON.stringify(payload),
    signal,
  })

  // Read as text first so we can still surface non-JSON error bodies.
  const rawText = await response.text()
  let raw: any = null
  try {
    raw = rawText ? JSON.parse(rawText) : null
  } catch {
    raw = null
  }

  if (!response.ok) {
    // Both proxies relay `{ error: { message, ... } }`; Anthropic adds no
    // `code`/`failed_generation`, so the Groq-specific recovery paths simply
    // won't trigger for Claude (which is correct — it has no such failure mode).
    const errData =
      raw ?? { error: { message: rawText || `${response.status} ${response.statusText}` } }
    return { ok: false, status: response.status, statusText: response.statusText, data: errData }
  }

  const data = normalizeCompletionResponse(provider === "anthropic" ? fromAnthropicResponse(raw) : raw)
  return { ok: true, status: response.status, statusText: response.statusText, data }
}

function normalizeToolCall(raw: any): AgentToolCall | null {
  if (!raw || typeof raw !== "object") return null
  const fn = raw.function && typeof raw.function === "object" ? raw.function : raw
  const name = typeof fn.name === "string" ? fn.name.trim() : ""
  if (!name) return null
  let args = fn.arguments ?? fn.parameters ?? raw.arguments
  if (args == null) args = "{}"
  else if (typeof args !== "string") {
    try {
      args = JSON.stringify(args)
    } catch {
      args = "{}"
    }
  }
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : `call-${name}`,
    type: "function",
    function: { name, arguments: args },
  }
}

/**
 * Shape the chat-completions body for the target provider.
 * DeepSeek thinking mode requires `reasoning_content` on every assistant
 * message whenever `tools` are present; Groq should not receive that field.
 */
export function prepareOutgoingChatBody(
  provider: AgentProviderId,
  body: Record<string, any>
): Record<string, any> {
  if (provider === "anthropic") return toAnthropicRequest(body)
  if (provider === "deepseek") return prepareDeepSeekRequest(body)
  return stripAssistantReasoningFields(body)
}

/**
 * DeepSeek thinking mode + tools: every assistant turn must echo
 * `reasoning_content` (empty string if the turn had no CoT). Historical
 * `reasoning` (Groq's name) is promoted when `reasoning_content` is absent.
 */
export function prepareDeepSeekRequest(body: Record<string, any>): Record<string, any> {
  const messages = Array.isArray(body.messages) ? body.messages : []
  const prepared = messages.map((message: any) => {
    if (!message || typeof message !== "object") return message
    const { reasoning, reasoning_content, ...rest } = message
    if (rest.role !== "assistant") return rest
    const fromContent =
      typeof reasoning_content === "string" ? reasoning_content : ""
    const fromGroq = typeof reasoning === "string" ? reasoning : ""
    return {
      ...rest,
      reasoning_content: fromContent || fromGroq,
    }
  })
  const { reasoning_format, ...rest } = body
  return { ...rest, messages: prepared }
}

function stripAssistantReasoningFields(body: Record<string, any>): Record<string, any> {
  const messages = Array.isArray(body.messages) ? body.messages : []
  return {
    ...body,
    messages: messages.map((message: any) => {
      if (!message || typeof message !== "object") return message
      const { reasoning, reasoning_content, ...rest } = message
      return rest
    }),
  }
}

/** Map provider-specific message fields onto the OpenAI shape the loop expects. */
export function normalizeOpenAiMessage(message: any): any {
  if (!message || typeof message !== "object") return message
  const normalized = { ...message }

  const reasoningContent =
    typeof normalized.reasoning_content === "string" ? normalized.reasoning_content.trim() : ""
  if (reasoningContent) {
    const existing =
      typeof normalized.reasoning === "string" ? normalized.reasoning.trim() : ""
    normalized.reasoning = existing ? `${existing}\n\n${reasoningContent}` : reasoningContent
    // Keep `reasoning_content` so the tool loop can echo it back to DeepSeek.
    normalized.reasoning_content = reasoningContent
  }

  if (Array.isArray(normalized.tool_calls)) {
    normalized.tool_calls = normalized.tool_calls
      .map(normalizeToolCall)
      .filter((call: AgentToolCall | null): call is AgentToolCall => call !== null)
    if (normalized.tool_calls.length === 0) delete normalized.tool_calls
  }

  return normalized
}

export function normalizeCompletionResponse(raw: any): any {
  if (!Array.isArray(raw?.choices)) return raw
  return {
    ...raw,
    choices: raw.choices.map((choice: any) => ({
      ...choice,
      message: normalizeOpenAiMessage(choice?.message),
    })),
  }
}

